import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { todayKey } from '@/domain/date';
import { createTimesProvider, recordDay, type TimesSettings } from '@bonyaan/prayer-times';
import { createTranslator } from '@/i18n/translator';
import { createBackend, type Preferences, type Profile, type ProfileDraft } from '@/ports';
import { DEFAULT_PREFERENCES } from '@/ports/preferences';
import { resolveScheme } from '@/theme/resolve';
import { skinForAge, themeClassName, type Skin } from '@/theme/types';
import { AppStateContext, type AppStateValue } from './context';

const backend = createBackend();

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [preferences, setPreferences] = useState<Preferences>(DEFAULT_PREFERENCES);
  const [skinPreview, setSkinPreview] = useState<Skin | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [p, prefs] = await Promise.all([backend.identity.current(), backend.preferences.get()]);
      if (cancelled) return;
      setProfile(p);
      setPreferences(prefs);
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const timesProvider = useMemo(() => createTimesProvider(preferences.times), [preferences.times]);
  const times = useMemo(() => timesProvider.forDate(todayKey()), [timesProvider]);
  const i18n = useMemo(() => createTranslator(preferences.locale), [preferences.locale]);

  const skin = skinPreview ?? preferences.skinOverride ?? skinForAge(profile?.age ?? 8);

  // Two things follow the clock rather than a render: the record day turns at
  // Fajr, and Auto turns to Day there too. Without a tick, an app left open
  // through either simply never noticed. `visibilitychange` as well, because
  // timers do not fire in a sleeping tab.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const mark = () => setNow(new Date());
    const timer = window.setInterval(mark, 60_000);
    document.addEventListener('visibilitychange', mark);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', mark);
    };
  }, []);

  const scheme = resolveScheme(preferences.scheme, times, now);
  const today = recordDay(times.fajr, now);

  // The theme axes and the language both live on <html>: one theme, and one
  // direction, for every screen, portal and the browser's own scroll gutter.
  // This is the only place they are written.
  useEffect(() => {
    const root = document.documentElement;
    root.className = themeClassName(skin, scheme, preferences.accent, preferences.fun);
    root.lang = i18n.locale;
    root.dir = i18n.dir;
  }, [skin, scheme, preferences.accent, preferences.fun, i18n]);

  const saveProfile = useCallback(async (draft: ProfileDraft) => {
    setProfile(await backend.identity.save(draft));
    setSkinPreview(null);
  }, []);

  const previewSkin = useCallback((next: Skin | null) => setSkinPreview(next), []);

  const updatePreferences = useCallback(async (patch: Partial<Preferences>) => {
    setPreferences(await backend.preferences.set(patch));
  }, []);

  const updateTimes = useCallback(async (patch: Partial<TimesSettings>) => {
    // Read the stored settings rather than trusting the caller's copy, so two
    // updates in flight merge instead of the later one clobbering the earlier.
    const current = await backend.preferences.get();
    setPreferences(await backend.preferences.set({ times: { ...current.times, ...patch } }));
  }, []);

  const value: AppStateValue = useMemo(
    () => ({
      ready,
      backend,
      profile,
      preferences,
      skin,
      scheme,
      times,
      now,
      today,
      timesProvider,
      i18n,
      previewSkin,
      saveProfile,
      updatePreferences,
      updateTimes,
    }),
    [
      ready,
      profile,
      preferences,
      skin,
      scheme,
      times,
      now,
      today,
      timesProvider,
      i18n,
      previewSkin,
      saveProfile,
      updatePreferences,
      updateTimes,
    ],
  );

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}
